import { FunctionComponent } from 'react'

import styled, { css, useTheme } from 'styled-components'

import { mobileCSS } from '@measures/responsive'
import translate from '@i18n'
import IconButton from '@components/Buttons/IconButton'
import PrimaryCTAButton from '@components/Buttons/PrimaryCTAButton'
import SecondaryCTAButton from '@components/Buttons/SecondaryCTAButton'

interface WelcomePageProps {
  onClose: () => void
  onClickNext: () => void
}

const StyledWrapper = styled.div`
  ${({
    theme: {
      colors: { fillDefault },
      spacing: { spacing16, spacing24, spacing40, spacing96 },
    },
  }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;

    max-width: 632px;
    width: auto;
    margin: auto 0;

    background-color: ${fillDefault};
    padding: ${spacing40} ${spacing96};
    gap: ${spacing24};
    ${mobileCSS(css`
      padding: ${spacing24} ${spacing16};
      width: calc(100% - 2 * ${spacing16});
    `)}
  `}
`

const StyledHeaderWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing12 },
    },
  }) => css`
    display: flex;
    flex-direction: row;
    gap: ${spacing12};
    align-items: flex-start;
  `}
`

const StyledHeaderText = styled.div`
  ${({
    theme: {
      typography: {
        subheads: {
          xlarge: { bundledCSS: xlargeSubheadCSS },
          large1: { bundledCSS: large1SubheadCSS },
        },
      },
      colors: { textDefault },
    },
  }) => css`
    ${xlargeSubheadCSS};
    color: ${textDefault};
    flex-grow: 1;
    ${mobileCSS(large1SubheadCSS)}
  `}
`

const StyledButtonWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing12 },
    },
  }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${spacing12};
  `}
`

const StyledPrimaryButton = styled(PrimaryCTAButton)`
  width: 162px;
  ${mobileCSS(css`
    width: 100%;
  `)}
`

const StyledSecondaryButton = styled(SecondaryCTAButton)`
  width: 162px;
  ${mobileCSS(css`
    width: 100%;
  `)}
`

const WelcomePage: FunctionComponent<WelcomePageProps> = ({
  onClickNext,
  onClose,
}) => {
  const theme = useTheme()
  return (
    <StyledWrapper>
      <StyledHeaderWrapper>
        <StyledHeaderText>
          {translate('blickPlusOnboardingModal.welcomePage')}
        </StyledHeaderText>
        <IconButton
          ariaLabel="Onboarding Modal Close"
          onClick={onClose}
          iconName="xmark-large"
          iconSize={24}
          iconColor={theme.colors.iconDefault}
          buttonSize={24}
        />
      </StyledHeaderWrapper>
      <StyledButtonWrapper>
        <StyledPrimaryButton onClick={onClickNext} size="large">
          {translate('blickPlusOnboardingModal.welcomePage.nextButton')}
        </StyledPrimaryButton>
        <StyledSecondaryButton onClick={onClose} size="large">
          {translate('blickPlusOnboardingModal.welcomePage.cancelButton')}
        </StyledSecondaryButton>
      </StyledButtonWrapper>
    </StyledWrapper>
  )
}

export default WelcomePage
